import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BugReportService } from './bug-report.service';
import { ClickupApiService } from '../../../@core/clickup-api.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { HpDialogRef } from '@shared/standalone-components/hp-dialog/hp-dialog.types';
import { HpSelectComponent, HpSelectOption } from '@shared/standalone-components/hp-select/hp-select.component';
import { downloadBase64Image } from '../../../@core/utils/download-image';
import { DateNow } from '../../../@core/constants/time.defaults';

@Component({
  selector: 'app-bug-report',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, HpSelectComponent],
  template: `
    <div class="flex flex-col gap-5 p-5">

      <!-- Screenshot zone -->
      @if (!imagePath && !screenshotLoading) {
        <button
          type="button"
          class="w-full h-16 rounded-lg border border-dashed border-black/[0.15] bg-hp-panel
                 flex items-center justify-center gap-2 text-hp-muted hover:bg-hp-panel-deep
                 transition-colors font-hp-sans text-[10.5px] tracking-[0.6px] uppercase"
          (click)="takeScreenshot()"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>
          </svg>
          Attach screenshot
        </button>
      }

      @if (screenshotLoading) {
        <div class="w-full h-16 rounded-lg bg-hp-panel border border-black/[0.08] flex items-center justify-center gap-2 font-hp-sans text-[10.5px] text-hp-muted">
          <svg class="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          Capturing…
        </div>
      }

      @if (imagePath) {
        <div class="flex flex-col gap-2">
          <img [src]="imagePath" class="w-full rounded-lg border border-black/[0.10]" />
          <div class="flex gap-2">
            <button
              type="button"
              class="h-7 px-3 rounded-md bg-transparent text-hp-ink2 border border-black/[0.10] font-hp-sans text-[10.5px] hover:bg-hp-panel-deep transition-colors"
              (click)="downloadImage()"
            >Download</button>
            <button
              type="button"
              class="h-7 px-3 rounded-md bg-transparent text-hp-muted border border-black/[0.10] font-hp-sans text-[10.5px] hover:bg-hp-panel-deep transition-colors"
              (click)="imagePath = null"
            >Remove</button>
          </div>
        </div>
      }

      <!-- Form -->
      <form [formGroup]="bugForm" class="flex flex-col gap-4">

        <!-- Title -->
        <div class="flex flex-col gap-1.5">
          <label class="font-hp-sans text-[10px] text-hp-muted tracking-[0.6px] uppercase">
            Title <span class="text-hp-crit">*</span>
          </label>
          <input
            formControlName="title"
            type="text"
            placeholder="e.g., Save button not working"
            class="h-9 w-full rounded-md border bg-hp-chip px-3 font-hp-sans text-[11.5px] text-hp-ink
                   placeholder:text-hp-muted2 outline-none transition-colors
                   focus:border-hp-accent/60 focus:ring-1 focus:ring-hp-accent/20"
            [class.border-hp-crit]="bugForm.controls.title.invalid && bugForm.controls.title.touched"
            [class.border-black/[0.12]]="!(bugForm.controls.title.invalid && bugForm.controls.title.touched)"
          />
          @if (bugForm.controls.title.invalid && bugForm.controls.title.touched) {
            <span class="font-hp-sans text-[10px] text-hp-crit tracking-[0.4px]">Required — enter a short title</span>
          }
        </div>

        <!-- Reason -->
        <div class="flex flex-col gap-1.5">
          <label class="font-hp-sans text-[10px] text-hp-muted tracking-[0.6px] uppercase">
            Type <span class="text-hp-crit">*</span>
          </label>
          <app-hp-select
            formControlName="reason"
            [options]="reasonOptions"
            placeholder="Select type…"
          />
          @if (bugForm.controls.reason.invalid && bugForm.controls.reason.touched) {
            <span class="font-hp-sans text-[10px] text-hp-crit tracking-[0.4px]">Required — select a type</span>
          }
        </div>

        <!-- Description -->
        <div class="flex flex-col gap-1.5">
          <label class="font-hp-sans text-[10px] text-hp-muted tracking-[0.6px] uppercase">
            Description <span class="text-hp-crit">*</span>
          </label>
          <textarea
            formControlName="description"
            rows="5"
            placeholder="Describe the issue or suggestion…"
            class="w-full rounded-md border bg-hp-chip px-3 py-2.5 font-hp-sans text-[11.5px] text-hp-ink
                   placeholder:text-hp-muted2 outline-none transition-colors resize-none
                   focus:border-hp-accent/60 focus:ring-1 focus:ring-hp-accent/20"
            [class.border-hp-crit]="bugForm.controls.description.invalid && bugForm.controls.description.touched"
            [class.border-black/[0.12]]="!(bugForm.controls.description.invalid && bugForm.controls.description.touched)"
          ></textarea>
          @if (bugForm.controls.description.invalid && bugForm.controls.description.touched) {
            <span class="font-hp-sans text-[10px] text-hp-crit tracking-[0.4px]">Required — describe the issue</span>
          }
        </div>

      </form>

      <!-- Submit -->
      <div class="flex justify-end">
        <button
          type="button"
          class="h-[30px] px-4 rounded-md bg-hp-ink text-hp-paper font-hp-sans text-[10.5px] tracking-[1px] font-bold uppercase hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          [disabled]="submitting"
          (click)="submit()"
        >
          @if (submitting) {
            <svg class="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
          }
          {{ submitting ? 'Sending…' : 'Submit' }}
        </button>
      </div>

    </div>
  `,
})
export class BugReportComponent implements OnInit {
  @Input() dialogRef?: HpDialogRef;

  private _bugSrv = inject(BugReportService);
  private _ticketServ = inject(ClickupApiService);
  private _notifications = inject(NzNotificationService);
  private _cdr = inject(ChangeDetectorRef);

  imagePath: string | null = null;
  screenshotLoading = false;
  submitting = false;

  reasonOptions: HpSelectOption[] = [
    { label: 'Critical Malfunction', value: 'Critical Malfunction' },
    { label: 'Minor Bug', value: 'Minor Bug' },
    { label: 'Feature Request', value: 'Feature Request' },
    { label: 'Comment', value: 'Comment' },
  ];

  bugForm = new FormGroup({
    title: new FormControl('', Validators.required),
    reason: new FormControl('', Validators.required),
    description: new FormControl('', Validators.required),
  });

  ngOnInit(): void {}

  async takeScreenshot(): Promise<void> {
    const el = document.getElementById('dashboard') ?? document.body;

    this.screenshotLoading = true;
    this._cdr.markForCheck();

    // Hide all hp-dialog host elements so the overlay doesn't appear in the screenshot
    const dialogHosts = Array.from(
      document.querySelectorAll<HTMLElement>('hp-dialog')
    );
    dialogHosts.forEach((h) => (h.style.visibility = 'hidden'));

    try {
      // Give the browser one frame to apply the visibility change
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      this.imagePath = await this._bugSrv.takeSnapshot(el);
      new Audio('assets/sounds/camera.mp3').play();
    } catch (e) {
      console.error(e);
    } finally {
      dialogHosts.forEach((h) => (h.style.visibility = ''));
      this.screenshotLoading = false;
      this._cdr.markForCheck();
    }
  }

  async downloadImage(): Promise<void> {
    if (!this.imagePath) return;
    downloadBase64Image(this.imagePath, `Shiptwin-screenshot-${DateNow()}.png`);
  }

  async submit(): Promise<void> {
    bugForm_markAllTouched(this.bugForm);
    this._cdr.markForCheck();
    if (!this.bugForm.valid) return;

    this.submitting = true;
    this._cdr.markForCheck();
    try {
      await this._ticketServ.createTicket({
        title: this.bugForm.value.title!,
        reason: this.bugForm.value.reason!,
        description: this.bugForm.value.description!,
        image: this.imagePath,
      });
      this._notifications.info('Success', 'Suggestion/Report sent to our team');
      this.dialogRef?.close();
    } catch (e) {
      this._notifications.error('Error', 'Error creating suggestion');
      console.error(e);
    } finally {
      this.submitting = false;
      this._cdr.markForCheck();
    }
  }
}

function bugForm_markAllTouched(group: FormGroup): void {
  Object.values(group.controls).forEach((c) => c.markAsTouched());
}
